import moment from 'moment';
import { CALENDAR_DATE_FORMAT, VALIDITY_STATUS, getMaintenanceExpiry, toServerCalendarDate } from './ExpiryRule';

// The 6.0 trial plan: its machines keep their Trial badge after the trial removal (Technical specification §12.2).
const SOFTWARE_ONLY_TRIAL_PLAN_NAME = 'software only trial';

const DURATION_PARTS = Object.freeze([
	{ unit: 'years', label: 'Year' },
	{ unit: 'months', label: 'Month' },
	{ unit: 'days', label: 'Day' },
]);

// `maintenance` is the base maintenance GET /licensing/products/batch returns for a machine: its plan, start, end,
// duration and renewals, with expiresOn the end of the last renewal. null means the machine has none, and undefined
// that the batch did not return the product (a failed call, an omitted id or an older rest-api).
const getRenewals = (maintenance) => {
	return Array.isArray(maintenance?.renewals) ? maintenance.renewals : [];
};

export const isSoftwareOnlyTrial = (maintenance) => {
	return maintenance?.plan?.customerFacingName?.toLowerCase() === SOFTWARE_ONLY_TRIAL_PLAN_NAME;
};

// The term whose plan and SerenITy entitlement hold today: the renewal whose dates contain today, the last renewal
// once the whole maintenance has ended, else the base. null without a maintenance.
export const getCurrentMaintenanceTerm = (maintenance) => {
	if (!maintenance) {
		return null;
	}

	const renewals = getRenewals(maintenance);
	const today = moment().format(CALENDAR_DATE_FORMAT);
	const renewalInForce = renewals.find((renewal) => {
		const startDate = toServerCalendarDate(renewal.startDate);
		const endDate = toServerCalendarDate(renewal.endDate);

		return startDate && endDate && startDate <= today && today <= endDate;
	});

	if (renewalInForce) {
		return renewalInForce;
	}

	const expiresOn = toServerCalendarDate(maintenance.expiresOn);

	if (renewals.length && expiresOn && today > expiresOn) {
		return renewals[renewals.length - 1];
	}

	return maintenance;
};

export const isMaintenanceExpired = (maintenance) => {
	return getMaintenanceExpiry(maintenance?.expiresOn, null)?.status === VALIDITY_STATUS.EXPIRED;
};

// The whole maintenance (base and renewals) as "3 Years", "1 Year, 6 Months" or "15 Days"; null when no term has
// a duration. Each term is added in its own unit, so 36 months read as 3 Years rather than as a day count.
export const formatMaintenanceDuration = (maintenance) => {
	if (!maintenance) {
		return null;
	}

	const total = moment.duration();
	let hasDuration = false;

	[maintenance, ...getRenewals(maintenance)].forEach((term) => {
		if (Number.isFinite(term.duration) && term.duration > 0 && term.durationType) {
			total.add(term.duration, term.durationType);
			hasDuration = true;
		}
	});

	if (!hasDuration) {
		return null;
	}

	return DURATION_PARTS
		.map(({ unit, label }) => {
			const amount = total.get(unit);

			return amount > 0 ? `${amount} ${label}${amount === 1 ? '' : 's'}` : null;
		})
		.filter(Boolean)
		.join(', ') || null;
};

// Folds the batch maintenance into each machine (see the note on `maintenance` above), for the pages that read
// the machines as a list rather than through the Inventory's product map.
export const withMaintenance = (machines, productsByMachineId) => {
	return machines.map((machine) => {
		return { ...machine, maintenance: productsByMachineId?.[machine.id]?.maintenance };
	});
};
